import React, {
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { styled } from "@mui/material/styles";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { useInView } from "react-intersection-observer";
import { IconWithToolTip } from "../common";
import {
  Button,
  Chip,
  Drawer,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Rating,
  Stack,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SortIcon from "@mui/icons-material/Sort";
import Attachments from "./attachments";
import Comments from "./comments";
import { getDateTime } from "../../utils/data";
import PreviewImageModal from "./previewAttachmentModal";
import Excel from "exceljs";
import { saveAs } from "file-saver";
import { UserContext } from "../../context/userContext";
import { UserLoginInfo } from "../../types/context";
import { appLogger, Http } from "../../utils";
import ThumbUpAltTwoToneIcon from "@mui/icons-material/ThumbUpAltTwoTone";
import ThumbDownAltTwoToneIcon from "@mui/icons-material/ThumbDownAltTwoTone";
import Mailing from "./mailing/mailingDrawer";
import MailingAnalytics from "./mailing/mailingAnalyticsDrawer";
import { enableInsights, enableAnalytics } from "../../utils/config";
import { FEEDBACK_TYPE_FEEDBACK, IFeedbackType, UserData } from "../../model";
import ReportAccordian from "./ReportAccordian";
import { useNavigate } from "react-router-dom";
import TicketsTable from "./ticketsComponents/TicketsTable";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, .05)"
      : "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

interface ReportProps {
  userData: UserData[];
  ticketsData: any;
  detectDateChange: any;
  detectFilterApplied: any;
  pageType: string;
  getSignedUrl: (url: string) => Promise<string>;
  isFetching: boolean;
  searchKeyword: string;
  filterLastEvaluatedKey: object;
  chipFilter: (
    i: number,
    data: string,
    label: JSX.Element,
    keyName: string,
    feedbackType: IFeedbackType
  ) => void;
  insightSelected: { keyName: string; keyWordType: string };
  insightsData: {
    data: {
      NEGATIVE: { [key: string]: any };
      NEUTRAL: { [key: string]: any };
      POSITIVE: { [key: string]: any };
    };
  };
  proVerUrl: string;
  filterUrlData: string;
  addFilterUrlData: string;
  fetchMoreUserData: any;
  fetchMoreRowsPerPagedatas: any;
  contextProVersion: { value: string; version: string; title: string }[];
  product: string;
  version: string;
  setChartData: any;
  handleSearchTyping: any;
  search: any;
  handleKeyPress: any;
  searchClear: any;
  rowsPerPage: any;
  globalStartDateValue: any;
  globalEndDateValue: any;
  fetchSortWiseData: any;
}

const Reports = (props: ReportProps) => {
  appLogger.info({
    infoObj: {
      method: "Reports",
      component: "feedback",
      fileName: "reports.tsx",
      props: props,
    },
  });
  const {
    userData,
    ticketsData,
    pageType,
    getSignedUrl,
    isFetching,
    filterUrlData,
    addFilterUrlData,
    searchKeyword,
    filterLastEvaluatedKey,
    chipFilter,
    insightSelected,
    insightsData,
    proVerUrl,
    fetchMoreUserData,
    contextProVersion,
    detectDateChange,
    detectFilterApplied,
    fetchMoreRowsPerPagedatas,
    handleSearchTyping,
    search,
    handleKeyPress,
    searchClear,
    rowsPerPage,
    globalStartDateValue,
    globalEndDateValue,
    fetchSortWiseData,
  } = props;
  const [open, setOpen] = React.useState(false);
  const [mailOpen, setMailOpen] = React.useState(false);
  const [detailRow, setDetailRow] = React.useState<UserData | any>("");
  const [sortOrder, setSortOrder] = React.useState(false);
  const [openImgPrvModal, setOpenImgprvw] = React.useState(false);
  const [ImgUrl, setImgUrl] = React.useState("");
  const [data, setData] = useState<UserData[]>(userData);
  const [ticketsTableData, setTicketsTableData] = useState<any>(ticketsData);
  const navigate = useNavigate();

  const [sortBy, setSortBy] = useState("Modified Date");
  const [update, setUpdate] = useState(false);
  const { userInfo, enabledFeatures } = useContext(
    UserContext
  ) as UserLoginInfo;
  const { ref, inView } = useInView();

  const [attachmentType, setAttachmentType] = React.useState("");

  const viewAttachmentClicked = async (
    attachmentData: string,
    type: string
  ) => {
    const url = await getSignedUrl(attachmentData);
    if (url) {
      setOpenImgprvw(true);
    }
    setImgUrl(url);
    setAttachmentType(type);
  };
  const closeImgPrvModalFn = () => {
    setOpenImgprvw(false);
  };

  useEffect(() => {
    if (
      inView &&
      filterLastEvaluatedKey &&
      Object.keys(filterLastEvaluatedKey).length > 0
    ) {
      //constraint for infinite scroll (affects filter query) && tableData.length > 0
      fetchMoreUserData("");
    }
    return () => {
      // can't do anything to cancel fetch more.
    };
  }, [inView]);

  useEffect(() => {
    if (
      inView &&
      filterLastEvaluatedKey &&
      Object.keys(filterLastEvaluatedKey).length > 0
    ) {
      //constraint for infinite scroll (affects filter query) && tableData.length > 0
      fetchMoreUserData("");
    }
    return () => {
      // can't do anything to cancel fetch more.
    };
  }, [inView]);

  const fetchMoreTabledatas = (pageDatas: any) => {
    fetchMoreUserData(pageDatas);
  };
  const fetchMoreRowsPerPageTabledatas = (pageDatas: any) => {
    fetchMoreRowsPerPagedatas(pageDatas);
  };
  const sortTrigger = (pageDatas: any) => {
    fetchSortWiseData(pageDatas);
  };

  const typeOptions = pageType == "FEEDBACK" ? "Rating" : "Severity";
  const sortingOptions = [
    "Created Date",
    "Modified Date",
    "Category",
    typeOptions,
    "Email",
    "Version",
  ];
  const workbook = new Excel.Workbook();

  const columns = [
    { header: "Source IP", key: "sourceIP" },
    {
      header: "Platform Name",
      key: "platformName",
    },
    {
      header: "Platform Version",
      key: "platformVersion",
    },
    { header: "Created On", key: "createdOn" },
    { header: "Context Details", key: "contextDetails" },
    { header: "BP Analytics Details", key: "bpAnalyticsDetails" },
    { header: "Product Rating ", key: "productRating" },
    { header: "Product Version", key: "productVersion" },
    { header: "User", key: "userId" },
    { header: "ID", key: "id" },
    { header: "Product ID", key: "productId" },
    { header: "Feedback Type", key: "feedbackType" },
    { header: "Feedback Category", key: "feedbackCategory" },
    {
      header: "Attachment",
      key: "attachment",
    },
    {
      header: "Family",
      key: "family",
    },
  ];

  const saveExcel = async () => {
    appLogger.info(
      {
        infoObj: {
          method: "saveExcel",
          component: "feedback",
          fileName: "reports.tsx",
          filterData: { filterUrlData, addFilterUrlData },
        },
      },
      "Excel Data"
    );

    const datewiseFilter = `&startDate=${
      globalStartDateValue ? globalStartDateValue : ""
    }&endDate=${globalEndDateValue ? globalEndDateValue : ""}`;

    const excelData = await Http.get<{
      Items?: { Items?: { [key: string]: any }[] };
    }>({
      url: `/api/v2/userFeedback/${pageType}?${proVerUrl}&${
        filterUrlData || ""
      }${
        addFilterUrlData || ""
      }${datewiseFilter}&search=${searchKeyword}&items=${ticketsTableData?.Count}`,
      state: userInfo,
    });
    appLogger.info(
      {
        infoObj: {
          method: "saveExcel",
          component: "feedback",
          fileName: "reports.tsx",
          excelData: excelData,
        },
      },
      "Excel Data"
    );
    if (excelData?.Items?.Items) {
      excelData.Items.Items.forEach((element) => {
        const l = JSON.parse(element.platformInfo);
        const media = element.feedbackMedia;
        element["platformName"] = l && l.platformName ? l.platformName : "-";
        element["platformVersion"] =
          l && l.platformVersion ? l.platformVersion : "-";
        element["family"] =
          l && l.platformOs && l.platformOs.family ? l.platformOs.family : "-";
        element["createdOn"] = new Date(element.createdOn);
        element["contextDetails"] =
          l.contextDetails != null || undefined ? l.contextDetails : "No Data";
        element["attachment"] =
          media.audio.length > 0
            ? "Audio"
            : media.file.length > 0
            ? "File"
            : media.image.length > 0
            ? "image"
            : media.video.length > 0
            ? "video"
            : "No Attachment";
      });

      try {
        // const fileName = EXPORT_NAME;
        // creating one worksheet in workbook
        const worksheet = workbook.addWorksheet("Worksheet-1");
        // add worksheet columns
        // each columns contains header and its mapping key from data
        worksheet.columns = columns;
        // updated the font for first row.
        worksheet.getRow(1).font = { bold: true };
        // loop through all of the columns and set the alignment and width.
        worksheet.columns.forEach((column) => {
          column.width = 100;
          column.alignment = { horizontal: "left" };
        });
        // loop through data and add each one to worksheet
        excelData.Items.Items.forEach((singleData) => {
          worksheet.addRow(singleData);
        });

        // loop through all of the rows and set the outline style.
        worksheet.eachRow({ includeEmpty: false }, (row: Excel.Row) => {
          // loop through the non-empty cells in each row to apply border
          row.eachCell((cell) => {
            // apply border
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        });

        // write the content using writeBuffer
        const buf = await workbook.xlsx.writeBuffer();
        // download the processed file
        const currentTime = new Date().toISOString();
        let fileName = "CuVo_Bug_";
        if (pageType == "FEEDBACK") {
          fileName = "CuVo_Feedback_";
        }
        saveAs(new Blob([buf]), `${fileName}${currentTime}.xlsx`);
      } catch (error) {
        appLogger.error({
          errObj: {
            method: "saveExcel",
            component: "feedback",
            fileName: "reports.tsx",
            err: error,
          },
        });
      } finally {
        // removing worksheet's instance to create new one
        workbook.removeWorksheet("Worksheet-1");
      }
    }
  };

  const getBPAnalyticsData = async (feedbackId: string, date: number) => {
    appLogger.info(
      {
        infoObj: {
          method: "getBPAnalyticsData",
          component: "feedback",
          fileName: "reports.tsx",
          filterData: { filterUrlData, addFilterUrlData },
        },
      },
      "BP Analytics Data"
    );
    let analyticsData: any = [];
    const hours: any = 120; //5days
    try {
      analyticsData = await Http.get({
        url: `/api/v2/bpanalytics/${feedbackId}/${date}/${hours}`,
        state: userInfo,
      });
      appLogger.info(
        {
          infoObj: {
            method: "getBPAnalyticsData",
            component: "feedback",
            fileName: "reports.tsx",
            analyticsData: analyticsData,
          },
        },
        "BP analytics Data"
      );
      return analyticsData["data"];
    } catch (err) {}
    return analyticsData;
  };

  // useEffect(() => {
  //   setData(userData);
  //   if (userData && userData.length > 0) {
  //     const sortedAsc = userData.sort((a, b) =>
  //       b.createdOn > a.createdOn ? 1 : -1
  //     );
  //     setData([...sortedAsc]);
  //   }
  // }, [userData]);

  useEffect(() => {
    setTicketsTableData(ticketsData);
    // if (ticketsData && ticketsData.length > 0) {
    //   const sortedAsc = ticketsData.sort((a: any, b: any) =>
    //     b.createdOn > a.createdOn ? 1 : -1
    //   );
    //   setTicketsTableData([...sortedAsc]);
    // }
  }, [ticketsData]);

  const handleDrawerOpen = async (index: number) => {
    setOpen(!open);
    if (data) {
      setDetailRow(data[index]);
    }
  };

  const handleReadMore = (index: number) => {
    navigate("/tickets", {
      state: {
        tab: 0,
        feedbackData: data[index],
      },
    });
    // Http.get({
    //   url: `/api/v2/userFeedback?id=${data[index]['id']}`,
    //   state: userInfo,
    // }).then((response: any) => {
    //   appLogger.info({
    //     infoObj: {
    //       method: "useEffect",
    //       component: "add-products",
    //       fileName: "index.tsx",
    //       productResponse: response,
    //     },
    //   });
    //   setTimeout(() => {
    //     navigate("/tickets", {
    //     state: {
    //       tab: 0,
    //       feedbackData: response.Items[0]
    //     },
    //   })},2000)

    // }).catch((error: any) => {
    //   appLogger.error({
    //     errObj: {
    //       method: "useEffect",
    //       component: "add-products",
    //       fileName: "index.tsx",
    //       err: error,
    //     },
    //   });
    //   // setFailure(true);
    // });
  };

  const handleMailDrawerOpen = async (index: number) => {
    setMailOpen(!open);
    const dataCopy = [...data];
    dataCopy[index].notificationCount = 0;
    setData(dataCopy);
    if (data) {
      const userDetails: string = data[index].userDetails || "{}";
      const user = JSON.parse(userDetails);
      // const user: any = data[index]['userDetails'] ? (typeof data[index]['userDetails'] == "string" ? JSON.parse(typeof data[index]['userDetails']) : typeof data[index]['userDetails']) : {};
      if (user && Object.keys(user).length > 0) {
        if (user["uuid"]) {
          const bp = await getBPAnalyticsData(
            data[index].id,
            data[index]["createdOn"]
          );
          data[index]["bpAnalyticsDetails"] = bp;
        }
      }
      setDetailRow(data[index]);
    }
  };

  const renderDetails = (details: { [key: string]: any }) => {
    return (
      <>
        {Object.keys(details).map((detailsKey) =>
          typeof details[detailsKey] === "object" ? (
            Object.keys(details[detailsKey]).map((key: string) => (
              <Grid item xs={12} md={4}>
                <Typography className="title-sm1">
                  {key === "family" ? "OS" : key}
                </Typography>
                <Typography className="info">
                  {details[detailsKey][key] || ""}
                </Typography>
              </Grid>
            ))
          ) : (
            <Grid item xs={12} md={4}>
              <Typography component="label">{detailsKey}</Typography>
              <Typography className="info">
                {details[detailsKey] || ""}
              </Typography>
            </Grid>
          )
        )}
      </>
    );
  };

  const renderChat = (chat: any) => {
    return (
      <>
        <Grid item xs={12} md={4} marginBottom={2} marginLeft={1}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ display: "flex" }}>
              <Typography component="label">Human: </Typography>
              <Typography
                className="info"
                style={{
                  paddingLeft: "4px",
                  lineHeight: "18px",
                  textTransform: "none",
                }}
              >
                {chat["human"]}{" "}
              </Typography>
            </div>
            <Typography component="label" style={{ fontSize: "10px" }}>
              {getDateTime(chat["startTime"])}{" "}
            </Typography>{" "}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ display: "flex", maxWidth: "390px" }}>
              <Typography component="label">Chatbot: </Typography>
              <Typography
                className="info"
                style={{
                  paddingLeft: "4px",
                  lineHeight: "18px",
                  textTransform: "none",
                }}
              >
                {chat["chatbot"]}{" "}
              </Typography>
            </div>
            <Typography component="label" style={{ fontSize: "10px" }}>
              {getDateTime(chat["endTime"])}{" "}
            </Typography>{" "}
          </div>
        </Grid>
      </>
    );
  };

  const renderChatDetails = (details: any) => {
    if (details && details.chats && details.chats.length > 0) {
      return (
        <>
          {details.chats.map((chat: any) => {
            return renderChat(chat);
          })}
        </>
      );
    }
    return <></>;
  };

  const selectData = (event: SelectChangeEvent) => {
    setSortBy(event.target.value);
  };

  const sortData = () => {
    setSortOrder(!sortOrder);
  };

  // useEffect(() => {
  //   setUpdate(true);
  //   if (sortBy === "Created Date") {
  //     if (!sortOrder) {
  //       const sortedAsc = data.sort((a, b) =>
  //         b.createdOn > a.createdOn ? 1 : -1
  //       );
  //       setData([...sortedAsc]);
  //     } else {
  //       const sortedDec = data.sort((a, b) =>
  //         a.createdOn > b.createdOn ? 1 : -1
  //       );
  //       setData([...sortedDec]);
  //     }
  //   } else if (sortBy === "Modified Date") {
  //     if (!sortOrder) {
  //       const sortedAsc = data?.sort((a, b) =>
  //         b.lastModifiedOn > a.lastModifiedOn ? 1 : -1
  //       );
  //       setData([...sortedAsc]);
  //     } else {
  //       const sortedDec = data.sort((a, b) =>
  //         a.lastModifiedOn > b.lastModifiedOn ? 1 : -1
  //       );
  //       setData([...sortedDec]);
  //     }
  //   } else if (sortBy === "Rating") {
  //     if (!sortOrder) {
  //       const sortedAsc = data.sort((a, b) => {
  //         if (b.productRating && a.productRating) {
  //           return b.productRating > a.productRating ? 1 : -1 ? 1 : -1;
  //         }
  //         return -1;
  //       });
  //       setData([...sortedAsc]);
  //     } else {
  //       const sortedDec = data.sort((a, b) => {
  //         if (b.productRating && a.productRating) {
  //           return a.productRating > b.productRating ? 1 : -1 ? 1 : -1;
  //         }
  //         return -1;
  //       });
  //       setData([...sortedDec]);
  //     }
  //   } else if (sortBy === "Severity") {
  //     if (!sortOrder) {
  //       const sortedAsc = data.sort((a, b) => {
  //         if (
  //           b.bugPriority &&
  //           typeof b.bugPriority == "string" &&
  //           a.bugPriority &&
  //           typeof a.bugPriority == "string"
  //         ) {
  //           return b.bugPriority?.toLowerCase() > a.bugPriority?.toLowerCase()
  //             ? 1
  //             : -1
  //             ? 1
  //             : -1;
  //         }
  //         return -1;
  //       });
  //       setData([...sortedAsc]);
  //     } else {
  //       const sortedDec = data.sort((a, b) => {
  //         if (
  //           b.bugPriority &&
  //           typeof b.bugPriority == "string" &&
  //           a.bugPriority &&
  //           typeof a.bugPriority == "string"
  //         ) {
  //           return a.bugPriority?.toLowerCase() > b.bugPriority?.toLowerCase()
  //             ? 1
  //             : -1
  //             ? 1
  //             : -1;
  //         }
  //         return -1;
  //       });
  //       setData([...sortedDec]);
  //     }
  //   } else if (sortBy === "Category") {
  //     if (!sortOrder) {
  //       const sortedAsc = data.sort((a, b) => {
  //         if (
  //           typeof b.feedbackCategory == "string" &&
  //           typeof a.feedbackCategory == "string"
  //         ) {
  //           return b.feedbackCategory?.toLowerCase() >
  //             a.feedbackCategory?.toLowerCase()
  //             ? 1
  //             : -1;
  //         }
  //         return -1;
  //       });
  //       setData([...sortedAsc]);
  //     } else {
  //       const sortedDec = data.sort((a, b) => {
  //         if (
  //           typeof b.feedbackCategory == "string" &&
  //           typeof a.feedbackCategory == "string"
  //         ) {
  //           return a.feedbackCategory?.toLowerCase() >
  //             b.feedbackCategory?.toLowerCase()
  //             ? 1
  //             : -1;
  //         }
  //         return -1;
  //       });
  //       setData([...sortedDec]);
  //     }
  //   } else if (sortBy === "Email") {
  //     if (sortOrder) {
  //       const sortedAsc = data.sort((a, b) => {
  //         if (b.userId && a.userId) {
  //           return b.userId?.toLowerCase() > a.userId?.toLowerCase() ? 1 : -1;
  //         }
  //         return -1;
  //       });
  //       setData([...sortedAsc]);
  //     } else {
  //       const sortedDec = data.sort((a, b) => {
  //         if (b.userId && a.userId) {
  //           return a.userId?.toLowerCase() > b.userId?.toLowerCase() ? 1 : -1;
  //         }
  //         return -1;
  //       });
  //       setData([...sortedDec]);
  //     }
  //   } else if (sortBy === "Version") {
  //     if (!sortOrder) {
  //       const sortedAsc = data.sort((a, b) =>
  //         b.productVersion > a.productVersion ? 1 : -1
  //       );
  //       setData([...sortedAsc]);
  //     } else {
  //       const sortedDec = data.sort((a, b) =>
  //         a.productVersion > b.productVersion ? 1 : -1
  //       );
  //       setData([...sortedDec]);
  //     }
  //   }
  //   setTimeout(() => {
  //     setUpdate(false);
  //   }, 0);
  // }, [sortBy, sortOrder]);

  const renderProVersion = (prodid: string, ver: string) => {
    return (
      contextProVersion &&
      contextProVersion.length > 0 &&
      contextProVersion.map((context) => {
        if (context.value == prodid && context.version == ver) {
          return context.title;
        }
      })
    );
  };

  const renderSentiment = (keyword: string) => {
    appLogger.info({
      infoObj: {
        method: "renderSentiment",
        component: "feedback",
        fileName: "reports.tsx",
        sentiment: keyword,
      },
    });
    let icon;
    switch (keyword) {
      case "POSITIVE":
        icon = (
          <ThumbUpAltTwoToneIcon
            style={{
              color: "#038229",
            }}
          />
        );
        break;
      case "NEGATIVE":
        icon = (
          <ThumbDownAltTwoToneIcon
            style={{
              color: "#f00",
            }}
          />
        );
        break;
      case "NEUTRAL":
        icon = (
          <ThumbDownAltTwoToneIcon
            style={{
              transform: "rotate(90deg)",
              color: "#5e6fed",
            }}
          />
        );
        break;
      default:
        icon = "";
    }
    return <>{icon}</>;
  };

  const renderLabel = (keyword: string, count: string) => {
    // const val = "Home Screen";
    return (
      <>
        {keyword}
        <span className="insight-chip-count">{count}</span>
      </>
    );
  };

  const ins = useRef<null | HTMLDivElement>(null);

  const renderClassName = (type: string, keyName: string, i: number) => {
    appLogger.info({
      infoObj: {
        method: "renderClassName",
        component: "feedback",
        fileName: "reports.tsx",
        data: { type, keyName, i, insightSelected },
      },
    });
    let val = "chip-list";
    if (
      insightSelected.keyName == keyName &&
      insightSelected.keyWordType == type
    ) {
      val = "highlight-chip";
    }
    return val;
  };

  return (
    <div className="user-reports ticket-list" ref={ins}>
      {enableInsights && enabledFeatures?.enableInsights ? (
        <Grid xs={12} lg={12} className="insight-card">
          <Box className="insight-item insight-fb-item">
            <h3 className="title insight-card-title">Insights</h3>
            <Stack
              direction="row"
              sx={{
                flexWrap: "wrap",
              }}
              className="insight-card-chip"
            >
              {insightsData &&
                insightsData.data &&
                insightsData.data.NEUTRAL &&
                Object.keys(insightsData.data.NEUTRAL).length == 0 &&
                insightsData.data &&
                insightsData.data.NEGATIVE &&
                Object.keys(insightsData.data.NEGATIVE).length == 0 &&
                insightsData.data &&
                insightsData.data.POSITIVE &&
                Object.keys(insightsData.data.POSITIVE).length == 0 && (
                  <Typography className="no-data">No data</Typography>
                )}
              {insightsData?.data?.POSITIVE &&
                Object.keys(insightsData.data.POSITIVE)
                  .slice(0, 5)
                  .map((keyName, i) => (
                    <Chip
                      key={i}
                      variant="outlined"
                      className={renderClassName("POSITIVE", keyName, i)}
                      onClick={() =>
                        chipFilter(
                          i,
                          "POSITIVE",
                          renderLabel(
                            keyName,
                            insightsData.data.POSITIVE[keyName]
                          ),
                          keyName,
                          FEEDBACK_TYPE_FEEDBACK
                        )
                      }
                      label={
                        <>
                          {renderLabel(
                            keyName,
                            insightsData.data.POSITIVE[keyName]
                          )}
                          <ThumbUpAltTwoToneIcon
                            style={{
                              marginLeft: 4,
                              color: "#038229",
                            }}
                          />
                        </>
                      }
                    />
                  ))}
              {insightsData?.data?.NEUTRAL &&
                Object.keys(insightsData.data.NEUTRAL)
                  .slice(0, 5)
                  .map((keyName, i) => (
                    <Chip
                      key={i}
                      variant="outlined"
                      onClick={() =>
                        chipFilter(
                          i,
                          "NEUTRAL",
                          renderLabel(
                            keyName,
                            insightsData.data.NEUTRAL[keyName]
                          ),
                          keyName,
                          FEEDBACK_TYPE_FEEDBACK
                        )
                      }
                      className={
                        "neutral-thumb-icon" +
                        " " +
                        renderClassName("NEUTRAL", keyName, i)
                      }
                      label={
                        <>
                          {renderLabel(
                            keyName,
                            insightsData.data.NEUTRAL[keyName]
                          )}
                          <ThumbDownAltTwoToneIcon
                            style={{
                              marginLeft: 4,
                              color: "#5e6fed",
                            }}
                          />
                        </>
                      }
                    />
                  ))}
              {insightsData?.data?.NEGATIVE &&
                Object.keys(insightsData.data.NEGATIVE)
                  .slice(0, 5)
                  .map((keyName, i) => (
                    <Chip
                      key={i}
                      variant="outlined"
                      className={renderClassName("NEGATIVE", keyName, i)}
                      onClick={() =>
                        chipFilter(
                          i,
                          "NEGATIVE",
                          renderLabel(
                            keyName,
                            insightsData.data.NEGATIVE[keyName]
                          ),
                          keyName,
                          FEEDBACK_TYPE_FEEDBACK
                        )
                      }
                      label={
                        <>
                          {renderLabel(
                            keyName,
                            insightsData.data.NEGATIVE[keyName]
                          )}
                          <ThumbDownAltTwoToneIcon
                            style={{
                              marginLeft: 4,
                              color: "#f00",
                            }}
                          />
                        </>
                      }
                    />
                  ))}
            </Stack>
          </Box>
        </Grid>
      ) : (
        <></>
      )}
      <Grid
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 2, mb: 1.5 }}
      >
        <h2 className="title">
          {pageType == "FEEDBACK"
            ? "Feedbacks"
            : pageType == "BUG_REPORT"
            ? "tickets"
            : "feature requests"}
        </h2>

        <Grid item sx={{ flexGrow: 1 }}>
          <Box
            className="sort-wrap"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <FormControl
              className="custom-select search-input-field"
              fullWidth
              sx={{ pr: 2 }}
            >
              <InputLabel>Search</InputLabel>
              <OutlinedInput
                title="Search is case-sensitive"
                sx={{
                  background: "white",
                  borderRadius: "8px",
                  minHeight: "36px",
                }}
                type="text"
                value={search}
                onChange={handleSearchTyping}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleKeyPress(e);
                  }
                }}
                id="search-input"
                endAdornment={
                  <>
                    {search && (
                      <InputAdornment
                        id="search-clear"
                        position="end"
                        onClick={() => searchClear()}
                      >
                        <IconButton
                          aria-label="search icon"
                          edge="end"
                          className="search-fb-icon"
                        >
                          <CloseIcon className="cursor-pointer" />
                        </IconButton>
                      </InputAdornment>
                    )}
                    <InputAdornment
                      component="div"
                      id="search-icon"
                      position="end"
                      onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                        search ? handleKeyPress(e) : searchClear();
                      }}
                    >
                      <IconButton
                        aria-label="search icon"
                        edge="end"
                        className="search-fb-icon"
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  </>
                }
                label="Search"
              />
            </FormControl>
            {data && data.length > 0 && (
              <Button
                id="export-btn"
                sx={{ mb: 0, mr: 1, whiteSpace: "nowrap" }}
                variant="outlined"
                onClick={() => {
                  //! saveExcel is a promise and should either be wrapped in a try catch block or extend the catch
                  saveExcel();
                }}
              >
                {pageType == "FEEDBACK"
                  ? "export feedback"
                  : pageType === "BUGS" || pageType === "BUG_REPORT"
                  ? "export tickets"
                  : "export feature request"}
              </Button>
            )}
            {/* <FormControl className="custom-select" fullWidth>
              <InputLabel>Sort By</InputLabel>
              <Select value={sortBy} onChange={selectData}>
                {sortingOptions.length > 0 &&
                  sortingOptions.map((item) => (
                    <MenuItem value={item} id={"sort" + item}>
                      {item}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl> */}
            {/* <Button
              className="sort-btn"
              variant="text"
              id="sort-order-btn"
              onClick={sortData}
            >
              {sortOrder ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
              <SortIcon />
            </Button> */}
          </Box>
        </Grid>
      </Grid>

      <Box
        sx={{ overflowY: "auto" }}
        className={
          update ? "reports-data-wrap reports-loaded" : "reports-data-wrap"
        }
      >
        <TicketsTable
          pageType={pageType}
          tableTitle={
            pageType == "FEEDBACK"
              ? "Feedbacks"
              : pageType == "BUG_REPORT"
              ? "Tickets"
              : "Feature Requests"
          }
          tabledata={ticketsTableData}
          fetchMoreTabledatas={fetchMoreTabledatas}
          isFetching={isFetching}
          filterLastEvaluatedKey={filterLastEvaluatedKey}
          contextProVersion={contextProVersion}
          detectDateChange={detectDateChange}
          detectFilterApplied={detectFilterApplied}
          fetchMoreRowsPerPageTabledatas={fetchMoreRowsPerPageTabledatas}
          sortTrigger={sortTrigger}
          setChartData={props.setChartData}
        />
        {/* {data &&
          data.map((feedbackData, index) => (
            <ReportAccordian
              key={`${feedbackData.id || ""}${index}`}
              feedbackData={feedbackData}
              index={index}
              pageType={pageType}
              renderProVersion={renderProVersion}
              renderSentiment={renderSentiment}
              getSignedUrl={getSignedUrl}
              handleMailDrawerOpen={handleMailDrawerOpen}
              handleDrawerOpen={handleDrawerOpen}
              inViewRef={ref}
              setChartData={props.setChartData}
            />
          ))}
        {isFetching ? (
          <div className="chart-item pagination-loader"></div>
        ) : (
          " "
        )} */}
      </Box>

      {/* {detailRow ? (
        <Mailing
          handleDrawerOpen={handleMailDrawerOpen}
          open={mailOpen}
          setOpen={setMailOpen}
          details={detailRow}
        />
      ) : (
        <div />
      )} */}
      {detailRow ? (
        <>
          {enableAnalytics ? (
            <MailingAnalytics
              handleDrawerOpen={handleMailDrawerOpen}
              open={mailOpen}
              setOpen={setMailOpen}
              details={detailRow}
            />
          ) : (
            <Mailing
              handleDrawerOpen={handleMailDrawerOpen}
              open={mailOpen}
              setOpen={setMailOpen}
              details={detailRow}
            />
          )}
        </>
      ) : (
        <div />
      )}
      {detailRow ? (
        <Drawer
          className="reports-popup"
          anchor="right"
          open={open}
          onClose={handleDrawerOpen}
        >
          <Box sx={{ width: "100%" }}>
            <Box
              className="title-wrap"
              sx={{
                display: "flex",
                alignItems: "center",
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography className="title">
                {pageType == "FEEDBACK" ? "feedback" : "tickets"}
              </Typography>

              <IconButton
                className="close-report"
                style={{ fontSize: "1rem" }}
                onClick={() => setOpen(!open)}
              >
                <IconWithToolTip message="Close" iconName="icon-close" />
              </IconButton>
            </Box>
            <Box className="user-reports">
              <Accordion defaultExpanded>
                <AccordionSummary
                  aria-controls={"panel1-content"}
                  id={"panel1-header"}
                >
                  <Typography className="title-sm1">basic details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Typography className="title-sm1">Date & Time</Typography>
                      <Typography className="info">
                        {detailRow.createdOn
                          ? getDateTime(detailRow.createdOn)
                          : ""}
                      </Typography>
                    </Grid>
                    {pageType != "FEATURE_REQUEST" ? (
                      <Grid item xs={12} md={5}>
                        <Typography className="title-sm1">
                          {detailRow.bugPriority == "" &&
                          detailRow.feedbackType === FEEDBACK_TYPE_FEEDBACK
                            ? "Rating"
                            : "Severity"}
                        </Typography>
                        {detailRow.bugPriority == "" &&
                        detailRow.feedbackType === FEEDBACK_TYPE_FEEDBACK ? (
                          <Rating
                            className="ratings"
                            name="read-only"
                            value={detailRow.productRating || 0}
                            readOnly
                          />
                        ) : (
                          <>
                            {detailRow.bugPriority ? (
                              <Chip
                                label={detailRow.bugPriority}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ) : (
                              <Typography className="info">Unknown</Typography>
                            )}
                          </>
                        )}
                      </Grid>
                    ) : (
                      <></>
                    )}
                    {enableInsights && enabledFeatures?.enableInsights && (
                      <Grid item xs={12} md={4}>
                        <Typography className="title-sm1">Sentiment</Typography>
                        {detailRow.sentiment ? (
                          <Typography className="version sentiment-field-list">
                            &nbsp;&nbsp; {renderSentiment(detailRow.sentiment)}
                          </Typography>
                        ) : (
                          "-"
                        )}
                      </Grid>
                    )}
                    <Grid item xs={12} md={3}>
                      <Typography className="title-sm1">Category</Typography>
                      <Typography className="info">
                        {detailRow.feedbackCategory
                          ? detailRow.feedbackCategory
                          : ""}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Typography className="report-info">
                        {detailRow.feedbackComments ? (
                          <Comments
                            comments={
                              detailRow.feedbackComments &&
                              typeof detailRow.feedbackComments === "string"
                                ? JSON.parse(detailRow.feedbackComments)
                                : undefined
                            }
                            category={detailRow.feedbackCategory}
                            feedbackType={detailRow.feedbackType}
                          />
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography className="title-sm1">Attachments</Typography>
                      {detailRow.feedbackMedia &&
                      Object.keys(detailRow.feedbackMedia).length > 0 ? (
                        <Attachments
                          media={detailRow.feedbackMedia}
                          getSignedUrl={getSignedUrl}
                          viewAttachmentClicked={viewAttachmentClicked}
                        />
                      ) : (
                        <div>
                          <Typography className="info no-attach">
                            No attachment
                          </Typography>
                        </div>
                      )}
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion defaultExpanded>
                <AccordionSummary
                  aria-controls={"panel2-content"}
                  id={"panel2-header"}
                >
                  <Typography className="title-sm1">
                    system information
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid>
                    <Typography className="title-sm1">
                      {detailRow.platformInfo &&
                      Object.keys(detailRow.platformInfo).length > 0 ? (
                        <>
                          <Grid container spacing={2}>
                            {renderDetails({
                              ...JSON.parse(detailRow.platformInfo),
                              ...{ SourceIP: detailRow.sourceIP },
                            })}
                          </Grid>
                        </>
                      ) : (
                        "-"
                      )}
                    </Typography>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion defaultExpanded>
                <AccordionSummary
                  aria-controls={"panel3-content"}
                  id={"panel3-header"}
                >
                  <Typography className="title-sm1">Chat Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid>
                    <Typography className="title-sm1">
                      {detailRow.feedbackComments &&
                      Object.keys(detailRow.feedbackComments).length > 0 ? (
                        <>
                          {" "}
                          <Grid>
                            {renderChatDetails(
                              JSON.parse(detailRow.feedbackComments)
                            )}
                          </Grid>
                        </>
                      ) : (
                        "-"
                      )}
                    </Typography>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion defaultExpanded>
                <AccordionSummary
                  aria-controls={"panel3-content"}
                  id={"panel3-header"}
                >
                  <Typography className="title-sm1">Page Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid>
                    <Typography className="title-sm1">
                      {detailRow.pageURL &&
                      Object.keys(detailRow.pageURL).length > 0 ? (
                        <>
                          {" "}
                          <Grid container spacing={2}>
                            {renderDetails(JSON.parse(detailRow.pageURL))}
                          </Grid>
                        </>
                      ) : (
                        "-"
                      )}
                    </Typography>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion defaultExpanded>
                <AccordionSummary
                  aria-controls={"panel4-content"}
                  id={"panel4-header"}
                >
                  <Typography className="title-sm1">User Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={12}>
                      <Typography className="title-sm1">Email</Typography>
                      <Typography className="info">
                        {detailRow.userId ? detailRow.userId : "Anonymous"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={12}>
                      <Typography>
                        {detailRow.userDetails &&
                        Object.keys(detailRow.userDetails).length > 0 ? (
                          <>
                            <Grid container spacing={2}>
                              {renderDetails(JSON.parse(detailRow.userDetails))}
                            </Grid>
                          </>
                        ) : (
                          "-"
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion defaultExpanded>
                <AccordionSummary
                  aria-controls={"panel5-content"}
                  id={"panel5-header"}
                >
                  <Typography className="title-sm1">Context Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={12}>
                      {detailRow.contextDetails &&
                      Object.keys(detailRow.contextDetails).length > 0 ? (
                        <Typography className="title-sm1">
                          <Grid container spacing={2}>
                            {detailRow.contextDetails &&
                            Object.keys(detailRow.contextDetails)
                              ? renderDetails(
                                  JSON.parse(detailRow.contextDetails)
                                )
                              : "Anonymous"}
                          </Grid>
                        </Typography>
                      ) : (
                        "-"
                      )}
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {detailRow.data && (
                <Accordion defaultExpanded>
                  <AccordionSummary
                    aria-controls={"panel6-content"}
                    id={"panel6-header"}
                  >
                    <Typography className="title-sm1">Keywords</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid
                        item
                        xs={12}
                        className={`keywords-chip ${
                          detailRow.sentiment?.toLowerCase() || ""
                        }`}
                      >
                        {detailRow.data.length > 0 &&
                          detailRow.data.map((item: ReactNode) => (
                            <Chip
                              label={item}
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          </Box>
        </Drawer>
      ) : (
        ""
      )}
      {attachmentType && attachmentType.length > 0 && (
        <PreviewImageModal
          attachmentUrl={ImgUrl}
          datas={detailRow}
          openAttachmentPrvModal={openImgPrvModal}
          closeAttachmentPrvModal={closeImgPrvModalFn}
          attachmentType={attachmentType}
        />
      )}
    </div>
  );
};

export default Reports;
